import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Pencil, Trash2, Ban, ShieldCheck, Search, Clock, Check } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import TerritoryPicker from "../../components/TerritoryPicker";

const ROLES = ["customer", "doctor", "admin", "distributor", "employee", "editor"];

const ROLE_BADGE = {
    customer: "bg-stone-100 text-stone-700",
    doctor: "bg-purple-50 text-purple-800",
    admin: "bg-sky-100 text-sky-800",
    editor: "bg-amber-50 text-amber-800",
    distributor: "bg-emerald-50 text-emerald-800",
    employee: "bg-rose-50 text-rose-800",
};

const TABS = [
    { value: "pending",     label: "Pending approval" },
    { value: "customer",    label: "Customers" },
    { value: "doctor",      label: "Doctors" },
    { value: "distributor", label: "Distributors" },
    { value: "employee",    label: "Employees" },
    { value: "editor",      label: "Editors" },
    { value: "admin",       label: "Admins" },
    { value: "all",         label: "All" },
];

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [items, setItems] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [tab, setTab] = useState("pending");
    const [q, setQ] = useState("");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [approving, setApproving] = useState(null);   // user being approved (modal)
    const [approveForm, setApproveForm] = useState({ role: "", designation_id: "" });

    const load = async () => {
        try {
            const [u, d] = await Promise.all([
                api.get("/admin/users"),
                api.get("/admin/designations").catch(() => ({ data: [] })),
            ]);
            setItems(u.data);
            setDesignations(d.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load users");
        }
    };
    useEffect(() => { load(); }, []);

    const designationName = (id) => designations.find((d) => d.id === id)?.name || "—";

    const isAdmin = me?.role === "admin";
    const isEditor = me?.role === "editor";
    const canManage = isAdmin || isEditor;     // edit / block / delete (with editor escalation guards on the backend)
    const canApprove = canManage;

    const counts = useMemo(() => {
        const c = { all: items.length, pending: 0 };
        for (const r of ROLES) c[r] = 0;
        for (const u of items) {
            c[u.role] = (c[u.role] || 0) + 1;
            if (u.pending_approval) c.pending += 1;
        }
        return c;
    }, [items]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return items.filter((u) => {
            if (tab === "pending") {
                if (!u.pending_approval) return false;
            } else if (tab !== "all" && u.role !== tab) {
                return false;
            }
            if (!needle) return true;
            return (u.name || "").toLowerCase().includes(needle)
                || (u.email || "").toLowerCase().includes(needle)
                || (u.phone || "").toLowerCase().includes(needle);
        });
    }, [items, tab, q]);

    const openEdit = (u) => {
        setEditing(u);
        setForm({
            name: u.name || "",
            email: u.email || "",
            phone: u.phone || "",
            role: u.role,
            active: u.active !== false,
            designation_id: u.designation_id || "",
            territory: u.territory || "",
        });
    };

    const save = async () => {
        if (!editing) return;
        if (!form.name?.trim()) return toast.error("Name is required");
        if (!form.email?.trim()) return toast.error("Email is required");
        setBusy(true);
        try {
            await api.put(`/admin/users/${editing.id}`, form);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const toggleBlock = async (u) => {
        const next = !(u.active === false);
        const verb = next ? "block" : "unblock";
        if (!window.confirm(`${verb[0].toUpperCase() + verb.slice(1)} ${u.email}?`)) return;
        try {
            await api.put(`/admin/users/${u.id}/block`, { active: !next });
            toast.success(next ? "User blocked" : "User unblocked");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const del = async (u) => {
        if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const approve = (u) => {
        // Open the approval modal — admin can confirm/edit role and designation
        setApproving(u);
        setApproveForm({
            role: u.role,
            designation_id: u.designation_id || "",
        });
    };

    const confirmApprove = async () => {
        if (!approving) return;
        setBusy(true);
        try {
            const body = { role: approveForm.role };
            if (approveForm.role === "employee") {
                body.designation_id = approveForm.designation_id || null;
            }
            await api.post(`/auth/admin/users/${approving.id}/approve`, body);
            toast.success(`${approving.email} approved`);
            setApproving(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-users">
            <Toaster position="top-center" />
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700">Members</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Users</h1>
                    <p className="text-sm text-stone-600 mt-2">Edit, block or delete any user. Filter by role using the tabs below.</p>
                </div>
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name, email or phone…"
                        className="rounded-none border-stone-300 pl-9 w-72"
                        data-testid="user-search"
                    />
                </div>
            </div>

            {isEditor && (
                <div className="bg-sky-50 border border-sky-200 px-4 py-3 mb-4 text-sm text-sky-900">
                    You're signed in as an <strong>editor</strong>. You can approve, edit, block and delete user accounts — except other admins. Promoting anyone to admin or editor is still restricted to admins.
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="rounded-none bg-white border border-sky-100 p-0 h-auto flex flex-wrap gap-0 w-full justify-start">
                    {TABS.map((t) => {
                        const cnt = counts[t.value] ?? 0;
                        const isPendingTab = t.value === "pending";
                        const highlight = isPendingTab && cnt > 0;
                        return (
                            <TabsTrigger
                                key={t.value}
                                value={t.value}
                                data-testid={`users-tab-${t.value}`}
                                className={`rounded-none px-4 py-2.5 text-sm data-[state=active]:bg-sky-700 data-[state=active]:text-white data-[state=active]:shadow-none border-r border-sky-100 last:border-r-0 ${highlight ? "text-amber-700 font-medium" : ""}`}
                            >
                                {t.label}
                                <span className={`ml-2 text-[10px] tracking-widest ${highlight ? "bg-amber-500 text-white px-1.5 py-0.5 rounded-full" : "opacity-70"}`}>{cnt}</span>
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                {TABS.map((t) => (
                    <TabsContent key={t.value} value={t.value} className="mt-4">
                        {t.value === "pending" && filtered.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-3 text-sm text-amber-900 flex items-start gap-2">
                                <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                                <div>
                                    <strong>{filtered.length}</strong> staff sign-up{filtered.length === 1 ? "" : "s"} awaiting your approval. Click <strong>Approve</strong> to grant sign-in access. You can change their role afterwards from <em>Edit</em> if needed.
                                </div>
                            </div>
                        )}
                        {t.value === "pending" && filtered.length === 0 && (
                            <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 mb-3 text-sm text-emerald-900 flex items-start gap-2">
                                <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                                No pending requests — every staff account is currently approved.
                            </div>
                        )}
                        <UsersTable
                            rows={filtered}
                            isAdmin={isAdmin}
                            canManage={canManage}
                            canApprove={canApprove}
                            me={me}
                            onEdit={openEdit}
                            onBlock={toggleBlock}
                            onDelete={del}
                            onApprove={approve}
                            designationName={designationName}
                            isPendingTab={t.value === "pending"}
                        />
                    </TabsContent>
                ))}
            </Tabs>

            <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
                <DialogContent className="rounded-none max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Edit user</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3 mt-2">
                            <Field label="Full name" v={form.name} on={(v) => setForm({ ...form, name: v })} testId="edit-name" />
                            <Field label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} testId="edit-email" type="email" />
                            <Field label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} testId="edit-phone" />
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-stone-500">Role</Label>
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={editing.id === me?.id}>
                                    <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="edit-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ROLES
                                            .filter(r => isAdmin || (r !== "admin" && r !== "editor"))
                                            .map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {editing.id === me?.id && <p className="text-[10px] text-stone-500 mt-1">You cannot change your own role.</p>}
                                {!isAdmin && <p className="text-[10px] text-stone-500 mt-1">Editors cannot promote anyone to admin or editor.</p>}
                            </div>
                            <div className="flex items-center justify-between border border-stone-200 px-3 py-2 mt-2">
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-stone-500">Active</div>
                                    <div className="text-[11px] text-stone-500">Inactive accounts cannot sign in.</div>
                                </div>
                                <Switch
                                    checked={form.active !== false}
                                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                                    disabled={editing.id === me?.id}
                                    data-testid="edit-active"
                                />
                            </div>

                            {form.role === "employee" && (
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-stone-500">Designation</Label>
                                    <Select value={form.designation_id || ""} onValueChange={(v) => setForm({ ...form, designation_id: v })}>
                                        <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="edit-designation"><SelectValue placeholder="— Not set —" /></SelectTrigger>
                                        <SelectContent>
                                            {designations.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {form.role === "employee" && (() => {
                                const desig = designations.find((d) => d.id === form.designation_id);
                                const isMR = desig && (desig.capabilities || []).includes("mr");
                                if (!isMR) return null;
                                return (
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-stone-500">
                                            Territory <span className="text-stone-400">(MR only)</span>
                                        </Label>
                                        <div className="mt-1">
                                            <TerritoryPicker
                                                value={form.territory || ""}
                                                onChange={(v) => setForm({ ...form, territory: v })}
                                                testIdBase="edit-territory"
                                            />
                                        </div>
                                        <p className="text-[11px] text-stone-500 mt-2">
                                            Pick the states this MR is responsible for. Their Dues page will only show customers in these states. Leave empty to disable the MR's dues page until you assign at least one state.
                                        </p>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)} className="rounded-none">Cancel</Button>
                        <Button onClick={save} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="edit-save">
                            {busy ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={!!approving} onOpenChange={(o) => { if (!o && !busy) setApproving(null); }}>
                <DialogContent className="rounded-none max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Approve staff sign-up</DialogTitle>
                    </DialogHeader>
                    {approving && (
                        <div className="space-y-3 mt-2">
                            <div className="bg-stone-50 border border-stone-200 px-3 py-2 text-xs">
                                <div><span className="text-stone-500">Name:</span> <span className="font-medium text-slate-900">{approving.name}</span></div>
                                <div><span className="text-stone-500">Email:</span> <span className="text-stone-700">{approving.email}</span></div>
                                {approving.phone && <div><span className="text-stone-500">Phone:</span> <span className="text-stone-700">{approving.phone}</span></div>}
                                {approving.signup_note && <div className="mt-1 italic text-stone-600">“{approving.signup_note}”</div>}
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-stone-500">Role</Label>
                                <Select value={approveForm.role} onValueChange={(v) => setApproveForm({ ...approveForm, role: v })}>
                                    <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="approve-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="employee">Employee (MR / Packing / Stock / Accounts)</SelectItem>
                                        <SelectItem value="editor">Editor</SelectItem>
                                        <SelectItem value="admin">Admin</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-[10px] text-stone-500 mt-1">User asked for: <strong>{approving.role}</strong>. You can change it before approving.</p>
                            </div>
                            {approveForm.role === "employee" && (
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-stone-500">Designation *</Label>
                                    <Select value={approveForm.designation_id} onValueChange={(v) => setApproveForm({ ...approveForm, designation_id: v })}>
                                        <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="approve-designation">
                                            <SelectValue placeholder="Choose what they can do" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {designations.length === 0 ? (
                                                <SelectItem value="__none__" disabled>No designations yet — create one first</SelectItem>
                                            ) : designations.map((d) => (
                                                <SelectItem key={d.id} value={d.id}>
                                                    {d.name}
                                                    {d.capabilities?.length > 0 && <span className="text-stone-400 ml-2 text-[10px] uppercase">· {d.capabilities.join(", ")}</span>}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-[10px] text-stone-500 mt-1">
                                        {approving.designation_id
                                            ? <>User requested <strong>{designationName(approving.designation_id)}</strong>.</>
                                            : "User didn't pick one — assign so they get the right portal access."}
                                        {" "}<a href="/admin/designations" target="_blank" rel="noopener" className="text-sky-700 underline">Manage designations</a>
                                    </p>
                                </div>
                            )}
                            <div className="bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-900">
                                On approval the user will be activated, can sign in immediately, and will receive an email confirmation (if SMTP is set).
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setApproving(null)} disabled={busy} className="rounded-none" data-testid="approve-cancel">Cancel</Button>
                        <Button
                            onClick={confirmApprove}
                            disabled={busy || (approveForm.role === "employee" && designations.length > 0 && !approveForm.designation_id)}
                            className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white"
                            data-testid="approve-confirm"
                        >
                            <Check className="w-4 h-4 mr-2" />{busy ? "Approving…" : "Approve & activate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const UsersTable = ({ rows, isAdmin, canManage, canApprove, me, onEdit, onBlock, onDelete, onApprove, designationName, isPendingTab = false }) => (
    <div className="bg-white border border-sky-100 overflow-x-auto">
        <table className="w-full text-sm">
            <thead>
                <tr className="bg-sky-50 border-b border-sky-100">
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Name</th>
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Email</th>
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Phone</th>
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Role</th>
                    {isPendingTab && <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Requested designation</th>}
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Joined</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {rows.length === 0 ? (
                    <tr><td colSpan={isPendingTab ? 8 : 7} className="p-10 text-center text-stone-500">No users match.</td></tr>
                ) : rows.map((u) => {
                    const blocked = u.active === false;
                    const pending = u.pending_approval === true;
                    const isSelf = u.id === me?.id;
                    return (
                        <tr key={u.id} className={`border-b border-stone-100 ${blocked && !pending ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                            <td className="p-3 font-medium text-slate-900">
                                {u.name}{isSelf && <span className="ml-2 text-[10px] uppercase tracking-widest text-sky-700">you</span>}
                                {u.signup_note && <div className="text-[11px] text-stone-500 mt-0.5 italic line-clamp-1" title={u.signup_note}>“{u.signup_note}”</div>}
                            </td>
                            <td className="p-3 text-stone-600">{u.email}</td>
                            <td className="p-3 text-stone-600">{u.phone || "—"}</td>
                            <td className="p-3">
                                <span className={`inline-block px-2 py-0.5 text-[11px] uppercase tracking-widest ${ROLE_BADGE[u.role] || "bg-stone-100 text-stone-700"}`}>{u.role}</span>
                            </td>
                            {isPendingTab && (
                                <td className="p-3 text-xs">
                                    {u.role === "employee"
                                        ? (u.designation_id
                                            ? <span className="text-stone-700">{designationName?.(u.designation_id)}</span>
                                            : <span className="text-stone-400 italic">none — assign on approval</span>)
                                        : <span className="text-stone-400">—</span>}
                                </td>
                            )}
                            <td className="p-3">
                                {pending
                                    ? <span className="inline-flex items-center text-amber-700 text-xs font-medium" data-testid={`user-pending-${u.id}`}><Clock className="w-3.5 h-3.5 mr-1" />Pending</span>
                                    : blocked
                                        ? <span className="inline-flex items-center text-red-700 text-xs"><Ban className="w-3.5 h-3.5 mr-1" />Blocked</span>
                                        : <span className="inline-flex items-center text-emerald-700 text-xs"><ShieldCheck className="w-3.5 h-3.5 mr-1" />Active</span>}
                            </td>
                            <td className="p-3 text-stone-500 text-xs whitespace-nowrap">{u.created_at?.slice(0, 10)}</td>
                            <td className="p-3 text-right space-x-3 whitespace-nowrap">
                                {pending && (
                                    <button
                                        onClick={() => onApprove(u)}
                                        disabled={!canApprove}
                                        className="text-emerald-700 hover:text-emerald-800 disabled:opacity-40 inline-flex items-center text-xs font-medium"
                                        data-testid={`user-approve-${u.id}`}
                                    >
                                        <Check className="w-3.5 h-3.5 mr-1" />Approve
                                    </button>
                                )}
                                {(() => {
                                    const isTargetAdmin = u.role === "admin";
                                    const editorBlocked = !isAdmin && isTargetAdmin;  // editor + admin target = no
                                    const canEdit = canManage && !editorBlocked;
                                    const canBlock = canManage && !editorBlocked && !isSelf && !pending;
                                    const canDelete = canManage && !editorBlocked && !isSelf;
                                    const adminLockedTitle = editorBlocked ? "Editors cannot modify admin accounts" : "";
                                    return (
                                        <>
                                            <button onClick={() => onEdit(u)} disabled={!canEdit} title={adminLockedTitle} className="text-sky-700 hover:text-sky-800 disabled:opacity-40 inline-flex items-center text-xs" data-testid={`user-edit-${u.id}`}>
                                                <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                                            </button>
                                            <button onClick={() => onBlock(u)} disabled={!canBlock} title={isSelf ? "Cannot block your own account" : pending ? "Approve first or delete the request" : adminLockedTitle} className="text-amber-700 hover:text-amber-800 disabled:opacity-40 inline-flex items-center text-xs" data-testid={`user-block-${u.id}`}>
                                                <Ban className="w-3.5 h-3.5 mr-1" />{blocked ? "Unblock" : "Block"}
                                            </button>
                                            <button onClick={() => onDelete(u)} disabled={!canDelete} title={isSelf ? "Cannot delete your own account" : adminLockedTitle} className="text-red-700 hover:text-red-800 disabled:opacity-40" data-testid={`user-delete-${u.id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </>
                                    );
                                })()}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

const Field = ({ label, v, on, type = "text", testId }) => (
    <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>
        <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid={testId} />
    </div>
);
